import { prettyPrintEnumValue } from "../utils/prettyPrintEnumValue";

/**
 * Canonical human-readable labels for submission statuses.
 *
 * This is the single source of truth — import it everywhere instead of
 * maintaining local label maps.
 */
export const SUBMISSION_STATUS_LABEL: Record<string, string> = {
  NOT_STARTED: "Not Started",
  IN_PROGRESS: "In Progress",
  ENCAMP_INITIAL_REVIEW: "Encamp Review",
  IN_REVIEW: "In Review",
  PROCESSING_SUBMISSION: "Awaiting Submission",
  FILING_COMPLETE: "Filing Complete",
  NOT_REPORTING: "Not Reporting",
};

const STATUS_EMOJI: Record<string, string> = {
  NOT_STARTED: "🔶",
  IN_PROGRESS: "🚧",
  ENCAMP_INITIAL_REVIEW: "⛺️",
  IN_REVIEW: "🔍",
  PROCESSING_SUBMISSION: "✅",
  FILING_COMPLETE: "🎉",
  NOT_REPORTING: "⭕️",
};

/**
 * Emoji-prefixed labels for frontend display.
 * Derived from SUBMISSION_STATUS_LABEL with emoji prefixes.
 */
export const SUBMISSION_STATUS_EMOJI_LABEL: Record<string, string> = Object.fromEntries(
  Object.entries(SUBMISSION_STATUS_LABEL).map(([key, label]) => [
    key,
    STATUS_EMOJI[key] ? `${STATUS_EMOJI[key]} ${label}` : label,
  ])
);

/**
 * Human-readable descriptions for submission state machine events.
 * Used in error messages when a transition is invalid.
 */
export const SUBMISSION_EVENT_LABEL: Record<string, string> = {
  START_PROGRESS: "start progress",
  SEND_TO_ENCAMP_REVIEW: "send to Encamp review",
  SEND_TO_CUSTOMER_REVIEW: "send to customer review",
  ALL_REVIEWS_APPROVED: "complete all reviews",
  REVIEWER_REJECTS: "reject review",
  MARK_FILING_COMPLETE: "mark filing complete",
  MARK_NOT_REPORTING: "mark as not reporting",
  RETURN_TO_IN_PROGRESS: "return to In Progress",
  SIGNED_DOCUMENT_REPLACED: "replace a prepared or signed document",
};

/**
 * Get the human-readable label for a submission status.
 * Falls back to prettyPrintEnumValue for unknown statuses.
 */
export function getStatusLabel(status: string): string {
  return SUBMISSION_STATUS_LABEL[status] ?? prettyPrintEnumValue(status, [], true);
}

/**
 * Get the human-readable description for a submission event type.
 * Falls back to prettyPrintEnumValue for unknown events.
 */
export function getEventLabel(eventType: string): string {
  return SUBMISSION_EVENT_LABEL[eventType] ?? prettyPrintEnumValue(eventType, [], true);
}
